// Banners.jsx — the pictures across the top of the app, and where they lead.
//
// ── THREE FIELDS THE OLD SCREEN COLLECTED AND THE SERVER THREW AWAY ──
// Description, the tap action's label, and the store scope. shop.MstBanners
// has no column for any of them: the write drops them and the read always
// answers empty. The old form asked for all three anyway, and the table had a
// 280px Description column that was blank on every row — an admin typed a
// description, saved, and watched it vanish with no error.
//
// They are gone from this screen. A field that cannot be stored is worse than
// a missing field, because the missing one is obvious and the stored-looking
// one is not. If the columns land later, the form grows again.
//
// ── THE BODY IS JSON, NEVER MULTIPART ──
// /admin/content/banners takes fields, not files. The cropped picture goes up
// through /admin/media/banners first and comes back as a relative uploads
// path; leaving `image` out of the body entirely keeps the picture the banner
// already has.
//
// ── DATES ARE CLEARED BY BEING PRESENT AND BLANK ──
// The server clears a date for any key the body carries, so start and end are
// always sent even when empty. That is how "permanent" is set on an edit.

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadImage } from "../../../api/media";
import {
  fetchBanners,
  createBanner,
  updateBanner,
  deleteBanner,
  toggleBannerStatus,
} from "../../../slice/banner/bannerSlice";
import { fetchCategoryDropdown } from "../../../slice/category/categorySlice";
import { fetchAllSubCategories } from "../../../slice/subCategory/subCategorySlice";
import BannerCropModal from "./BannerCropModal";
import {
  PageShell,
  DataTable,
  Button,
  Thumb,
  Toggle,
  RowActions,
  Modal,
  Field,
  ImagePicker,
  Combo,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const BASE_URL = process.env.REACT_APP_API_URL;
const PER_PAGE = 10;

/* Built-in app destinations. Keep in step with the service's COLLECTION_KEYS
   and the app's banner handler. */
const COLLECTIONS = [
  { id: "categories", name: "All categories" },
  { id: "best_sellers", name: "Best sellers" },
  { id: "popular", name: "Popular" },
  { id: "gi_tagged", name: "GI tagged" },
  { id: "wow", name: "WOW picks" },
  { id: "offers", name: "Offer zone" },
  { id: "live_mill", name: "Live mill" },
];

const LINK_TYPES = [
  { value: "none", label: "Nothing — just a picture" },
  { value: "collection", label: "A list or section" },
  { value: "category", label: "A category" },
  { value: "subcategory", label: "A sub category" },
  { value: "product", label: "A product" },
  { value: "url", label: "A web link" },
];

const pictureOf = (row) => {
  const raw = (row?.imageUrl || row?.image || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `${BASE_URL}/uploads/${raw.replace(/^\/+/, "").replace(/^uploads\//, "")}`;
};

/* yyyy-MM-dd for <input type="date">, empty when not set. */
const forInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

const blank = {
  id: null,
  name: "",
  startDate: "",
  endDate: "",
  linkType: "none",
  linkValue: "",
};

export default function Banners() {
  const dispatch = useDispatch();
  const { banners, loading, error, pagination } = useSelector((s) => s.banner);
  const categories = useSelector((s) => s.category?.dropdownCategories || []);
  const subCategories = useSelector((s) => s.subCategory?.allSubCategories || []);

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cropping, setCropping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    dispatch(fetchBanners({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  useEffect(() => {
    dispatch(fetchCategoryDropdown());
    dispatch(fetchAllSubCategories());
  }, [dispatch]);

  /* What the destination picker offers, for whichever kind was chosen. */
  const destinations = useMemo(() => {
    if (form.linkType === "collection") return COLLECTIONS;
    if (form.linkType === "category") return categories;
    if (form.linkType === "subcategory") return subCategories;
    return [];
  }, [form.linkType, categories, subCategories]);

  /* ── the form ──────────────────────────────────────────────────────────── */

  const openNew = () => {
    setForm(blank);
    setPicked(null);
    setPreview(null);
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      startDate: forInput(row.startDate),
      endDate: forInput(row.endDate),
      linkType: row.link?.type || "none",
      linkValue: row.link?.value || "",
    });
    setPreview(pictureOf(row) || null);
    setPicked(null);
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setForm(blank);
    setPicked(null);
    setPreview(null);
    setProblem("");
  };

  const cropped = (file) => {
    setPicked(file);
    setPreview(URL.createObjectURL(file));
    setCropping(null);
  };

  const save = async () => {
    if (!form.name.trim()) return setProblem("Give the banner a name.");

    /* The server refuses endDate <= startDate with invalid_window, so the form
       refuses it first and says which way round they go. */
    if (
      form.startDate &&
      form.endDate &&
      new Date(form.endDate) <= new Date(form.startDate)
    )
      return setProblem("The end date has to be after the start date.");

    if (form.linkType !== "none" && !form.linkValue.trim())
      return setProblem("Choose where tapping this banner should go.");

    setSaving(true);
    setProblem("");

    const body = {
      name: form.name.trim(),
      startDate: form.startDate || "",
      endDate: form.endDate || "",
      link: {
        type: form.linkType,
        value: form.linkType === "none" ? "" : form.linkValue.trim(),
      },
    };

    try {
      if (picked) body.image = await uploadImage(picked, "banners");

      if (form.id) {
        await dispatch(updateBanner({ id: form.id, payload: body })).unwrap();
      } else {
        await dispatch(createBanner(body)).unwrap();
        setPage(1);
      }
      dispatch(fetchBanners({ page, limit: PER_PAGE, search: query }));
      close();
    } catch (err) {
      setProblem(err || "That did not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await dispatch(deleteBanner(row.id)).unwrap();
      dispatch(fetchBanners({ page, limit: PER_PAGE, search: query }));
    } catch (err) {
      setProblem(err || "Could not delete it.");
    }
  };

  /* ── what a row's schedule says ────────────────────────────────────────── */

  const schedule = (row) => {
    const from = onDate(row.startDate);
    const to = onDate(row.endDate);
    if (!from && !to) return <span className="ui-muted">Always on</span>;
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    return `Until ${to}`;
  };

  const destination = (row) => {
    const type = row.link?.type || "none";
    if (type === "none") return <span className="ui-muted">Nothing</span>;
    const value = row.link?.value || "";
    if (type === "collection")
      return COLLECTIONS.find((c) => c.id === value)?.name || value;
    if (type === "category")
      return categories.find((c) => String(c.id) === String(value))?.name || value;
    if (type === "subcategory")
      return (
        subCategories.find((c) => String(c.id) === String(value))?.name || value
      );
    if (type === "url")
      return <span style={{ wordBreak: "break-all" }}>{value}</span>;
    return value;
  };

  const rows = banners || [];

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Banners"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} in rotation`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search banners",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Add banner
        </Button>
      }
    >
      {cropping ? (
        <BannerCropModal
          file={cropping}
          aspect={16 / 9}
          onCancel={() => setCropping(null)}
          onCropped={cropped}
        />
      ) : null}

      <DataTable
        columns={["", "Name", "Tapping it opens", "Showing", "Live", ""]}
        loading={loading}
        error={error || problem}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "No banner matches that" : "No banners yet"}
        emptyHint={
          query
            ? "Try a shorter search."
            : "Banners run across the top of the app's home screen."
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td style={{ width: 92 }}>
              <Thumb src={pictureOf(row)} alt={row.name} />
            </td>
            <td>
              <span className="ui-who__name">{row.name}</span>
            </td>
            <td>{destination(row)}</td>
            <td className="ui-num">{schedule(row)}</td>
            <td>
              <Toggle
                on={row.status !== false}
                onChange={() => dispatch(toggleBannerStatus(row.id))}
                label={
                  row.status !== false
                    ? "Live in the app — tap to take down"
                    : "Taken down — tap to publish"
                }
              />
            </td>
            <td>
              <RowActions
                onEdit={() => openEdit(row)}
                onDelete={() => remove(row)}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={form.id ? `Edit ${form.name}` : "Add banner"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : form.id ? "Save changes" : "Add it"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Name" required hint="For your own reference — customers never see it.">
          <input
            className="ui-input"
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. Diwali offers"
          />
        </Field>

        <Field label="Picture" hint="Cropped to 16:9, the shape the app's carousel uses.">
          <ImagePicker
            preview={preview}
            onPick={(file) => setCropping(file)}
            hint="PNG, JPG or WebP — wide, and legible at phone size"
          />
        </Field>

        <Field label="Tapping it opens">
          <select
            className="ui-select"
            value={form.linkType}
            onChange={(e) =>
              setForm((f) => ({ ...f, linkType: e.target.value, linkValue: "" }))
            }
          >
            {LINK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        {form.linkType === "url" ? (
          <Field label="Web address" required>
            <input
              className="ui-input"
              value={form.linkValue}
              onChange={set("linkValue")}
              placeholder="https://…"
            />
          </Field>
        ) : form.linkType === "product" ? (
          <Field
            label="Product id"
            required
            hint="From the product's own page in Item Master."
          >
            <input
              className="ui-input"
              value={form.linkValue}
              onChange={set("linkValue")}
              placeholder="Paste the product id"
            />
          </Field>
        ) : form.linkType !== "none" ? (
          <Field label="Destination" required>
            <Combo
              value={form.linkValue}
              onChange={(v) => setForm((f) => ({ ...f, linkValue: v }))}
              options={destinations}
              placeholder="Choose where it goes"
            />
          </Field>
        ) : null}

        <Field
          label="Showing"
          hint="Leave both blank and it runs until you take it down."
        >
          <div style={{ display: "flex", gap: 10 }}>
            <input
              className="ui-input"
              type="date"
              value={form.startDate}
              onChange={set("startDate")}
              aria-label="Start date"
            />
            <input
              className="ui-input"
              type="date"
              value={form.endDate}
              onChange={set("endDate")}
              aria-label="End date"
            />
          </div>
        </Field>
      </Modal>
    </PageShell>
  );
}
